import { useMemo, useCallback } from 'react';
import { createHookContext } from '@clover-util/react-context';
import { useGetPerksProgram } from 'hooks/useCeApi/useGetPerksProgram';
import { useMerchant } from './merchant';
import { Perk } from './Perks.model';
import { getCeApiHostname } from 'util/Environment';
import { useTranslation } from 'react-i18next';
import { useMerchantState } from './MerchantState';
import { useCustomer } from './customer/customer';

export const AVAILABLE_PERK_BACKGROUND = `${getCeApiHostname()}/2/asset/icon/burst_orange`;
export const INPROGRESS_PERK_BACKGROUND = `${getCeApiHostname()}/2/asset/icon/burst_brown`;
export const EMPTY_PERK_BACKGROUND = `${getCeApiHostname()}/2/asset/icon/burst_gray`;

export const { usePerks, PerksProvider } = createHookContext('Perks', () => {
  const { t } = useTranslation();
  const { merchant } = useMerchant();
  const { showPerksState } = useMerchantState();
  const { loading: perksFetchInProgress, perksProgram, error } = useGetPerksProgram(showPerksState, merchant?.merchantUuid);
  const { customer, isGetCustomerLoading, isLoggedIn } = useCustomer();

  // Hackiness to determine if loading has finished
  const isPerksLoading = (perksFetchInProgress || (perksProgram === undefined && error === undefined ));
  const isCustomerLoading = (isLoggedIn && isGetCustomerLoading);
  const loading = isPerksLoading || isCustomerLoading;

  const { memoedAvailablePerks, memoedInProgressPerks } = useMemo(() => {
    const programPointPerks = perksProgram?.pointsPerks?.map(({ pointsRequired, ...perk }) => ({
      ...perk,
      earned: 0,
      required: pointsRequired,
      perkType: 'POINT',
      backgroundIconUrl: INPROGRESS_PERK_BACKGROUND,
      available: false
    } as Perk)) ?? [];

    const programPunchPerks = perksProgram?.punchPerks?.map(({ punchesRequired, ...perk }) => ({
      ...perk,
      earned: 0,
      required: punchesRequired,
      perkType: 'PUNCHCARD',
      backgroundIconUrl: INPROGRESS_PERK_BACKGROUND,
      available: false
    } as Perk)) ?? [];

    const earnedPerks = customer?.earnedPerks?.map(perk => ({
      uuid: perk.uuid,
      iconUrl: perk.iconUrl,
      title: perk.title,
      backgroundIconUrl: perk.backgroundUrl,
      perkType: perk.type,
      earned: perk.earned,
      required: perk.required,
      available: perk.available,
      startsAt: perk.startsAt,
      endsAt: perk.endsAt,
      restrictions: perk.restrictions
    }) as Perk) ?? [];

    const programTypePerkFilter = (perk: Perk) => {
      const perkTypeToRemove = (perksProgram?.programType === 'POINT' ? 'PUNCHCARD': 'POINT');

      // filter out the perk type that is not currently part of merchant program
      return perk.perkType !== perkTypeToRemove;
    };

    const relevantEarnedPerks = earnedPerks.filter(programTypePerkFilter);
    const earnedAvailablePerks = relevantEarnedPerks.filter(perks => perks.available);
    const earnedInprogressPerks = relevantEarnedPerks.filter(perks => !perks.available);

    const uniquePerkFilterFn = (() => {
      const uniquePerkUuids: Set<string> = new Set();

      return (perk: Perk) => {
        if (uniquePerkUuids.has(perk.uuid)) return false;
        uniquePerkUuids.add(perk.uuid);

        return true;
      };
    })();

    const availablePerks: Perk[] = earnedAvailablePerks.filter(uniquePerkFilterFn);

    const relevantPerks = perksProgram?.programType === 'POINT' ? programPointPerks : programPunchPerks;

    const inProgressPerks: Perk[] = [...earnedInprogressPerks, ...relevantPerks]
      .filter(uniquePerkFilterFn)
      .map(perk => {
        const required = (perk.required ?? 0);
        switch (perk.perkType) {
          case 'POINT':
            return {
              ...perk,
              subtitle: t('perks.pointSubtitle', { count: required })
            };
          case 'PUNCHCARD':
            return {
              ...perk,
              subtitle: t('perks.punchSubtitle', { count: required })
            };
          default:
            return perk;
        }
      });

      return {
        memoedAvailablePerks: availablePerks,
        memoedInProgressPerks: inProgressPerks
      };
  }, [customer, perksProgram, t]);

  const getPerk = useCallback((uuid)=> [...memoedAvailablePerks,...memoedInProgressPerks].filter(perks => perks.uuid === uuid)[0],[memoedAvailablePerks, memoedInProgressPerks]);

  return {
    isLoading: useMemo(() => loading && !error, [loading, error]),
    atLeastOnePerk: useMemo(() => memoedAvailablePerks.length > 0 || memoedInProgressPerks.length > 0, [memoedAvailablePerks, memoedInProgressPerks]),
    availablePerks: memoedAvailablePerks,
    inProgressPerks: memoedInProgressPerks,
    merchantTimezone: merchant?.timezone,
    getPerk
  };
});
